import { ArrowDownToLine, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { ApiSchemas } from "@/shared/api";
import { InfoTooltip } from "@/shared/ui/info-tooltip";
import { Button } from "@/shared/ui/kit/button";
import { Price } from "@/shared/ui/price";

type NavUserBalanceProps = {
  amount: number;
  pendingDeposits?: number;
  compact?: boolean;
  onTopUp?: () => void;
};

export function NavUserBalance({
  amount,
  pendingDeposits,
  compact = false,
  onTopUp,
}: NavUserBalanceProps) {
  const { t } = useTranslation();

  const textSize = compact ? "text-[10px]" : "text-xs";
  const valueSize = compact ? "text-sm" : "text-base";
  const showPending = pendingDeposits !== undefined && pendingDeposits > 0;

  return (
    <div className="space-y-2">
      <div className="text-xs text-muted-foreground">{t("nav.balance")}</div>
      <div
        className={`grid ${showPending ? "grid-cols-2" : "grid-cols-1"} gap-2`}
      >
        <div className="flex items-center gap-2 rounded-lg bg-background border p-2">
          <button
            onClick={onTopUp}
            className="shrink-0 size-7 rounded-md bg-blue-600 flex items-center justify-center hover:bg-blue-700 transition-colors"
          >
            <Plus className="size-3.5 text-white" />
          </button>
          <div className="flex flex-col min-w-0">
            <span className={`${textSize} text-muted-foreground leading-tight`}>
              {t("balance.total")}
            </span>
            <span
              className={`${valueSize} font-bold text-primary leading-tight`}
            >
              <Price cents={amount} />
            </span>
          </div>
        </div>
        {showPending && (
          <div className="flex flex-col rounded-lg bg-background border p-2">
            <span className={`${textSize} text-muted-foreground leading-tight`}>
              Ожидают
            </span>
            <span
              className={`${valueSize} font-semibold text-amber-500 leading-tight`}
            >
              <Price cents={pendingDeposits} />
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

type NavUserBalanceMinimalProps = {
  balance: ApiSchemas["Balance"] | null;
  onTopUp?: () => void;
};

export function NavUserBalanceMinimal({
  balance,
  onTopUp,
}: NavUserBalanceMinimalProps) {
  const { t } = useTranslation();
  const amount = balance?.amount ?? 0;

  return (
    <div className="p-2">
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground inline-flex items-center gap-1.5">
          {t("nav.balance")}
          <InfoTooltip tKey="nav.info.balanceBreakdown" />
        </span>

        <div className="flex items-center gap-3">
          <span className="font-medium">
            <Price cents={amount} />
          </span>
          <Button size="icon" onClick={onTopUp} className="size-6">
            <Plus className="size-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

type NavStoreBalanceMinimalProps = {
  storeBalance: ApiSchemas["StoreBalance"];
  onWithdraw?: (availableBalance: number) => void;
};

export function NavStoreBalanceMinimal({
  storeBalance,
  onWithdraw,
}: NavStoreBalanceMinimalProps) {
  const { t } = useTranslation();
  const amount = storeBalance?.amount ?? 0;
  const available = storeBalance?.available ?? 0;

  return (
    <div className="p-2">
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">{t("nav.storeBalance")}</span>

        <div className="flex items-center gap-3">
          <span className="font-medium">
            <Price cents={amount} />
          </span>
          {available > 0 && (
            <Button
              size="icon"
              onClick={() => onWithdraw?.(available)}
              className="size-6"
            >
              <ArrowDownToLine className="size-3.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
